import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  Card,
  Icon,
  PieChart,
  PieChartDataPoint,
} from '@node-space/ui-components'
import { useUserCurrentCurrency } from 'pages/UserDetails/hooks/useUserCurrentCurrency'
import { Portfolio } from 'types/rewards'
import { useRewardsSheetContext } from '../context/RewardsSheetProvider'
import { useRewardsSummary } from '../hooks/useRewardsSummary'
import { RewardsActionIds } from '../types'
import { formatAmount } from '../utils'

export interface RewardsSummaryProps {
  userPortfolios: Portfolio[]
}

export const RewardsSummary = ({ userPortfolios }: RewardsSummaryProps) => {
  const userCurrency = useUserCurrentCurrency()
  const { t } = useTranslation()
  const { handleSheetOpen } = useRewardsSheetContext()

  const { portfoliosSummary, totalBalance, totalEarnings, totalPendingEarnings } =
    useRewardsSummary(userPortfolios)

  const handleRemoveAllocation = () => {
    handleSheetOpen({
      actionId: RewardsActionIds.REMOVE_ALLOCATION,
      header: t('rewards.removeAllocation.title'),
    })
  }

  return (
    <Card variant="static" emphasis full>
      <Box
        flexCol
        align="center"
        className="text-center lg:text-left gap-[27px] lg:grid lg:grid-cols-[2fr_185px_1fr]"
      >
        <AllocationSummary
          totalBalance={totalBalance}
          totalEarnings={totalEarnings}
          userCurrency={userCurrency}
        />
        <AllocationChart data={portfoliosSummary} />
        <PendingEarnings
          amount={totalPendingEarnings}
          currency={userCurrency}
          onRemoveAllocation={handleRemoveAllocation}
        />
      </Box>
    </Card>
  )
}

const PendingEarnings = ({
  amount,
  currency,
  onRemoveAllocation,
}: {
  amount: number
  currency: string
  onRemoveAllocation: () => void
}) => {
  const { t } = useTranslation()

  return (
    <Box flexCol justify="center" className="items-center lg:items-start">
      <p className="text-h3 text-foreground-prominent">{formatAmount(amount)}</p>
      <p className="text-body-regular-small text-foreground-prominent">
        {t('rewards.pendingEarnings', { currency })}
      </p>
      <Button variant="ghost" noPadding onClick={onRemoveAllocation} className="underline">
        {t('rewards.removeAllocation.removeAllocation')}
      </Button>
    </Box>
  )
}

const AllocationChart = ({ data }: { data: PieChartDataPoint[] }) => {
  return (
    <Box flexCol gapY={16} justify="center">
      <Box className="h-[180px] w-[180px] relative">
        <Box className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[220px] h-[220px]">
          <PieChart
            dataKey="value"
            data={data}
            Icon={<Icon size="large" name="SketchLogo" />}
            innerRadius="55%"
          />
        </Box>
      </Box>
    </Box>
  )
}

const AllocationSummary = ({
  totalBalance,
  totalEarnings,
  userCurrency,
}: {
  totalBalance: number
  totalEarnings: number
  userCurrency: string
}) => {
  const { t } = useTranslation()

  return (
    <Box flexCol justify="center" gapY={8} paddingY={8} className="md:min-w-[282px]">
      <p className="text-body-regular-small text-foreground-prominent">
        {t('rewards.totalAllocatedBalance')}
      </p>

      <Box flex align="center" gap={8}>
        <Avatar name={userCurrency} type="currency" />
        <p className="text-h1 text-foreground-prominent">{formatAmount(totalBalance)}</p>
      </Box>

      <Box flex align="center" gap={8}>
        <Icon name="SketchLogo" size="medium" />
        <p className="text-strong-small text-foreground-subdued">
          {t('rewards.allTimeEarnings', {
            currency: userCurrency,
            amount: formatAmount(totalEarnings),
          })}
        </p>
      </Box>
    </Box>
  )
}
